import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useTheme } from '@theme/useTheme';
import { useAuth } from '@contexts/AuthContext';
import { useFormData } from '@hooks/useFormData';
import Page0, { type Page0Handle } from './assessment/Page0';
import Page1, { type Page1Handle } from './assessment/Page1';
import Page2, { type Page2Handle } from './assessment/Page2';
import Page3, { type Page3Handle } from './assessment/Page3';
import ResultsPage from './assessment/ResultsPage';
import GradientBackground from '@components/ui/GradientBackground';
import ProfileModal from '@components/modals/ProfileModal';
import Spinner from '@components/ui/Spinner';

const Assessment: React.FC = () => {
  const { colors } = useTheme();
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showProfile, setShowProfile] = useState(false);

  const {
    formData,
    updateFormData,
    calculateAndSetRiskScore,
    resetFormData,
    isLoading,
    error,
    isInitialLoading,
  } = useFormData();

  // Reset assessment when navigating from results page
  useEffect(() => {
    if (location.state?.resetAssessment) {
      resetFormData();
      setCurrentPage(0);
      setShowResults(false);
      setShowModal(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      // Clear the state to prevent resetting on every render
      window.history.replaceState({}, document.title);
    }
  }, [location.state, resetFormData]);

  const page0Ref = useRef<Page0Handle>(null);
  const page1Ref = useRef<Page1Handle>(null);
  const page2Ref = useRef<Page2Handle>(null);
  const page3Ref = useRef<Page3Handle>(null);

  const pageRefs = [page0Ref, page1Ref, page2Ref, page3Ref];

  React.useEffect(() => {
    const handleProfileModal = () => setShowProfile(true);

    window.addEventListener('openProfileModal', handleProfileModal);

    return () => {
      window.removeEventListener('openProfileModal', handleProfileModal);
    };
  }, []);

  // Scroll to top when page changes
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  // Scroll to top when results page opens
  React.useEffect(() => {
    if (showResults) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [showResults]);

  const handleCloseProfile = () => {
    setShowProfile(false);
  };

  const handlePrevious = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = async () => {
    const currentPageRef = pageRefs[currentPage];

    if (currentPageRef.current?.validate()) {
      if (currentPage < 3) {
        setCurrentPage(currentPage + 1);
      } else {
        try {
          await calculateAndSetRiskScore();
          setShowResults(true);
        } catch (err) {
          console.error('Assessment submission failed:', err);
        }
      }
    }
  };

  const handleModalComplete = async () => {
    try {
      await calculateAndSetRiskScore();
      setShowResults(true);
    } catch (err) {
      console.error('Assessment submission failed:', err);
    }
  };

  const canGoPrevious = currentPage > 0;
  const canGoNext = currentPage < 3;

  if (isInitialLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: colors.background }}
      >
        <div className="text-center">
          <Spinner size="xl" color={colors.primary} className="mb-4" />
          <p style={{ color: colors.text }}>Loading assessment...</p>
        </div>
      </div>
    );
  }

  if (showResults) {
    return (
      <ResultsPage
        formData={formData}
        onRetake={() => {
          resetFormData();
          setCurrentPage(0);
          setShowResults(false);
          setShowModal(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      />
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: colors.background }}
    >
      {/* Header */}
      {currentPage < 4 && (
        <div className="relative pt-24 pb-16 sm:pt-32 sm:pb-20 lg:pt-40 lg:pb-24 mb-0 sm:mb-2 lg:mb-3 overflow-hidden">
          <GradientBackground position="top" height="medium" />

          <div className="px-6 sm:px-8 lg:px-12 xl:px-16 relative z-10">
            <div className="max-w-7xl mx-auto">
              <div className="text-center">
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                >
                  <motion.h1
                    className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight"
                    style={{ color: colors.heading }}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                  >
                    Sleep Apnea Assessment
                  </motion.h1>
                  <motion.p
                    className="text-lg sm:text-xl leading-relaxed font-medium max-w-3xl mx-auto"
                    style={{ color: colors.text }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
                  >
                    Complete our comprehensive assessment to understand your sleep health and get personalized recommendations.
                  </motion.p>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form Pages */}
      <div className="pb-6">
        {currentPage === 0 && (
          <Page0
            ref={page0Ref}
            formData={formData}
            updateFormData={updateFormData}
            onPrevious={handlePrevious}
            onNext={handleNext}
            currentPage={currentPage}
            canGoPrevious={canGoPrevious}
            canGoNext={canGoNext}
            isLoading={isLoading}
            error={error}
            showModal={showModal}
            setShowModal={setShowModal}
            onModalComplete={handleModalComplete}
          />
        )}

        {currentPage === 1 && (
          <Page1
            ref={page1Ref}
            formData={formData}
            updateFormData={updateFormData}
            onPrevious={handlePrevious}
            onNext={handleNext}
            currentPage={currentPage}
            canGoPrevious={canGoPrevious}
            canGoNext={canGoNext}
            isLoading={isLoading}
            error={error}
            showModal={showModal}
            setShowModal={setShowModal}
            onModalComplete={handleModalComplete}
          />
        )}

        {currentPage === 2 && (
          <Page2
            ref={page2Ref}
            formData={formData}
            updateFormData={updateFormData}
            onPrevious={handlePrevious}
            onNext={handleNext}
            currentPage={currentPage}
            canGoPrevious={canGoPrevious}
            canGoNext={canGoNext}
            isLoading={isLoading}
            error={error}
            showModal={showModal}
            setShowModal={setShowModal}
            onModalComplete={handleModalComplete}
          />
        )}

        {currentPage === 3 && (
          <Page3
            ref={page3Ref}
            formData={formData}
            updateFormData={updateFormData}
            onPrevious={handlePrevious}
            onNext={handleNext}
            currentPage={currentPage}
            canGoPrevious={canGoPrevious}
            canGoNext={canGoNext}
            isLoading={isLoading}
            error={error}
            showModal={showModal}
            setShowModal={setShowModal}
            onModalComplete={handleModalComplete}
          />
        )}
      </div>

      {/* Profile Modal */}
      {isAuthenticated && (
        <ProfileModal
          visible={showProfile}
          onClose={handleCloseProfile}
        />
      )}
    </div>
  );
};

export default Assessment;
